import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bell,
  Building,
  CalendarPlus,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  KeyRound,
  LogOut,
  Menu as MenuIcon,
  Search,
  ShieldAlert,
  UserCircle2,
  X,
} from 'lucide-react'
import { useAuth, usePerms } from '@/lib/auth'
import { api } from '@/lib/api'
import { cn, usePref } from '@/lib/utils'
import { relative } from '@/lib/format'
import type { Notification } from '@/api/types'
import { Avatar, Button, Kbd, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import { visibleNav } from './nav'
import { CommandPalette } from './CommandPalette'
import { Wordmark } from './Logo'
import { ThemeToggle } from './ThemeToggle'
import { OfflineBanner } from '@/components/offline'
import { staffName } from '@/lib/hooks'

export function AppShell() {
  const { me, logout, branchId, selectBranch } = useAuth()
  const { can, canAny, hasModule } = usePerms()
  const [collapsed, setCollapsed] = usePref('sidebar-collapsed', false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const nav = useMemo(() => visibleNav(canAny, hasModule), [canAny, hasModule])
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => setMobileOpen(false), [location.pathname])
  // Load the screens used during an outage now, so they still open if the connection drops later.
  useEffect(() => {
    const t = setTimeout(() => {
      void import('@/pages/patients/RegisterPatientPage')
      void import('@/pages/offline/OfflinePage')
    }, 3000)
    return () => clearTimeout(t)
  }, [])
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(true)
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [])

  if (!me) return null
  // The API refuses hospital data until a temporary password is replaced; go straight to that form.
  if (me.must_change_password && location.pathname !== '/app/profile') return <Navigate to="/app/profile?tab=password" replace />
  const branch = me.branches.find((b) => b.id === branchId)

  const sidebar = (
    <nav aria-label="Main" className="flex h-full flex-col">
      <div className={cn('flex h-16 items-center gap-2 border-b border-white/10 px-4', collapsed && 'lg:justify-center lg:px-0')}>
        <Link to="/app" aria-label="LumaHMS home">
          <Wordmark tone="dark" size="sm" hideTextOn={collapsed ? 'lg:hidden' : undefined} />
        </Link>
      </div>
      <div className="scroll-thin flex-1 overflow-y-auto px-3 py-4">
        {nav.map((g) => (
          <div key={g.label} className="mb-5">
            <p className={cn('mb-1.5 px-2.5 text-[11px] font-semibold tracking-wider text-[#99f6e4]/70 uppercase', collapsed && 'lg:sr-only')}>{g.label}</p>
            <ul className="space-y-0.5">
              {g.items.map((i) => (
                <li key={i.to}>
                  <NavLink
                    to={i.to}
                    end={i.end}
                    title={collapsed ? i.label : undefined}
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center gap-3 rounded-lg px-2.5 py-2 text-[13.5px] font-medium transition-colors',
                        isActive ? 'bg-white/12 text-white shadow-[inset_3px_0_0_var(--color-accent)]' : 'text-[#ccfbf1]/80 hover:bg-white/6 hover:text-white',
                        collapsed && 'lg:justify-center lg:px-0',
                      )
                    }
                  >
                    <i.icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
                    <span className={cn(collapsed && 'lg:sr-only')}>{i.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="hidden border-t border-white/10 p-3 lg:block">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex w-full items-center justify-center gap-2 rounded-lg py-2 text-[13px] text-[#ccfbf1]/75 hover:bg-white/6 hover:text-white"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronsRight className="h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" /> Collapse</>}
        </button>
      </div>
    </nav>
  )

  return (
    <div className="flex min-h-screen">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:rounded focus:bg-surface focus:px-3 focus:py-2">
        Skip to content
      </a>
      <aside className={cn('fixed inset-y-0 left-0 z-40 hidden bg-gradient-to-b from-sidebar to-sidebar-end lg:block', collapsed ? 'w-[72px]' : 'w-64')}>
        {sidebar}
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-ink-950/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-gradient-to-b from-sidebar to-sidebar-end animate-slide-in">
            <button className="absolute top-4 right-3 text-white" onClick={() => setMobileOpen(false)} aria-label="Close menu">
              <X className="h-5 w-5" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}
      <div className={cn('flex min-w-0 flex-1 flex-col', collapsed ? 'lg:pl-[72px]' : 'lg:pl-64')}>
        {me.tenant.is_demo && (
          <div className="no-print bg-amber-50 px-4 py-1.5 text-center text-xs font-medium text-amber-900 ring-1 ring-amber-200">
            Demonstration environment — every patient and record here is synthetic. Do not enter real patient information.
          </div>
        )}
        <OfflineBanner />
        <header className="no-print sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-ink-200/80 bg-surface/90 px-4 backdrop-blur md:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu" onClick={() => setMobileOpen(true)}>
            <MenuIcon className="h-5 w-5" />
          </Button>
          <div className="hidden min-w-0 items-center gap-2 md:flex">
            <span className="truncate text-sm font-semibold text-ink-900">{me.tenant.name}</span>
            {me.branches.length > 1 ? (
              <Menu>
                <MenuTrigger asChild>
                  <button className="flex items-center gap-1 rounded-md px-2 py-1 text-[13px] text-ink-500 hover:bg-ink-100">
                    <Building className="h-3.5 w-3.5" /> {branch?.name ?? 'All branches'}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </MenuTrigger>
                <MenuContent align="start">
                  <MenuLabel>Working branch</MenuLabel>
                  {me.branches.map((b) => (
                    <MenuItem key={b.id} onSelect={() => selectBranch(b.id)}>
                      <span className={cn(b.id === branchId && 'font-semibold text-brand-800')}>{b.name}</span>
                    </MenuItem>
                  ))}
                </MenuContent>
              </Menu>
            ) : (
              branch && <span className="text-[13px] text-ink-500">· {branch.name}</span>
            )}
          </div>
          <button
            onClick={() => setPaletteOpen(true)}
            className="ml-auto flex h-9 w-full max-w-sm items-center gap-2 rounded-lg border border-ink-200 bg-ink-50/80 px-3 text-[13px] text-ink-500 hover:border-ink-300 md:ml-6"
            aria-label={can('patients.read') ? 'Search patients and pages' : 'Search pages'}
          >
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">{can('patients.read') ? 'Search patients, MRN, phone…' : 'Search pages…'}</span>
            <span className="hidden sm:inline">
              <Kbd>⌘K</Kbd>
            </span>
          </button>
          <div className="flex items-center gap-1 md:ml-auto">
            <ThemeToggle className="mr-1.5" />
            {!me.must_change_password && (
              <>
                <TasksButton />
                <NotificationsButton />
              </>
            )}
            <Menu>
              <MenuTrigger asChild>
                <button className="ml-1 flex items-center gap-2 rounded-lg py-1 pr-2 pl-1 hover:bg-ink-100" aria-label="Account menu">
                  <Avatar name={me.full_name} />
                  <span className="hidden max-w-[150px] text-left leading-tight 2xl:block">
                    <span className="block truncate text-[13px] font-medium whitespace-nowrap text-ink-900">{staffName(me)}</span>
                    <span className="block truncate text-[11px] whitespace-nowrap text-ink-500">{me.roles[0] ?? me.designation}</span>
                  </span>
                </button>
              </MenuTrigger>
              <MenuContent>
                <MenuLabel>
                  <span className="block text-ink-800">{staffName(me)}</span>
                  <span className="block font-normal">{me.email}</span>
                  <span className="mt-1 block font-normal">{me.roles.join(', ')}</span>
                </MenuLabel>
                <MenuSeparator />
                <MenuItem icon={<UserCircle2 className="h-4 w-4" />} onSelect={() => navigate('/app/profile')}>
                  Profile & security
                </MenuItem>
                <MenuItem icon={<KeyRound className="h-4 w-4" />} onSelect={() => navigate('/app/profile?tab=password')}>
                  Change password
                </MenuItem>
                {hasModule('hr') && (
                  <MenuItem icon={<CalendarPlus className="h-4 w-4" />} onSelect={() => navigate('/app/profile?tab=leave')}>
                    My leave
                  </MenuItem>
                )}
                <MenuSeparator />
                <MenuItem
                  icon={<LogOut className="h-4 w-4" />}
                  danger
                  onSelect={async () => {
                    await logout()
                    navigate('/login')
                  }}
                >
                  Sign out
                </MenuItem>
              </MenuContent>
            </Menu>
          </div>
        </header>
        {me.must_change_password && (
          <div className="flex items-center justify-center gap-2 bg-rose-50 px-4 py-2 text-[13px] text-rose-900 ring-1 ring-rose-200">
            <ShieldAlert className="h-4 w-4" /> You signed in with a temporary password. Set your own password to continue.
          </div>
        )}
        <main id="main" className="mx-auto w-full max-w-[1500px] flex-1 px-4 py-6 md:px-8 md:py-8">
          <Outlet />
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} nav={nav} />
    </div>
  )
}

function NotificationsButton() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ items: Notification[]; unread: number }>('/notifications', { limit: 25 }),
    refetchInterval: 30_000,
  })
  const readAll = useMutation({ mutationFn: () => api.post('/notifications/read-all'), onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }) })
  const readOne = useMutation({ mutationFn: (id: string) => api.post(`/notifications/${id}/read`), onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }) })
  const unread = data?.unread ?? 0
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} className="relative">
          <Bell className="h-[18px] w-[18px]" />
          {unread > 0 && (
            <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0">
        <div className="flex items-center justify-between border-b border-ink-100 px-4 py-3">
          <p className="text-sm font-semibold">Notifications</p>
          {unread > 0 && (
            <button className="text-xs font-medium text-brand-700 hover:underline" onClick={() => readAll.mutate()}>
              Mark all read
            </button>
          )}
        </div>
        <ul className="scroll-thin max-h-[420px] overflow-y-auto">
          {(data?.items ?? []).length === 0 && <li className="px-4 py-8 text-center text-sm text-ink-500">You are all caught up.</li>}
          {data?.items.map((n) => (
            <li key={n.id}>
              <button
                className={cn('flex w-full gap-3 border-b border-ink-100 px-4 py-3 text-left hover:bg-ink-50', !n.read_at && 'bg-brand-50/40')}
                onClick={() => {
                  if (!n.read_at) readOne.mutate(n.id)
                  if (n.link) navigate(n.link)
                }}
              >
                <span
                  className={cn(
                    'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                    n.severity === 'critical' ? 'bg-rose-500' : n.severity === 'warning' ? 'bg-amber-500' : 'bg-sky-500',
                    n.read_at && 'opacity-0',
                  )}
                  aria-hidden
                />
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium text-ink-900">
                    {n.severity === 'critical' && <span className="mr-1 text-rose-700">Critical ·</span>}
                    {n.title}
                  </span>
                  {n.body && <span className="mt-0.5 block text-xs text-ink-500">{n.body}</span>}
                  <span className="mt-1 block text-[11px] text-ink-500">{relative(n.created_at)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}

function TasksButton() {
  const navigate = useNavigate()
  const { data } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.get<{ items: { id: string; title: string; link: string | null; kind: string; priority: number }[]; counts: Record<string, number> }>('/tasks'),
    refetchInterval: 45_000,
  })
  const n = data?.items.length ?? 0
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Work queue, ${n} open`} className="relative">
          <ClipboardList className="h-[18px] w-[18px]" />
          {n > 0 && (
            <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-[#1c1917]">
              {n > 99 ? '99+' : n}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0">
        <div className="border-b border-ink-100 px-4 py-3">
          <p className="text-sm font-semibold">Team work queue</p>
          <p className="text-xs text-ink-500">Open items for the teams you belong to</p>
        </div>
        <ul className="scroll-thin max-h-[380px] overflow-y-auto">
          {n === 0 && <li className="px-4 py-8 text-center text-sm text-ink-500">No open work items.</li>}
          {data?.items.slice(0, 30).map((t) => (
            <li key={t.id}>
              <button className="flex w-full items-center gap-2 border-b border-ink-100 px-4 py-2.5 text-left text-[13px] hover:bg-ink-50" onClick={() => t.link && navigate(t.link)}>
                {t.priority > 0 && <span className="rounded bg-rose-50 px-1 text-[10px] font-bold text-rose-700 ring-1 ring-rose-200">URGENT</span>}
                <span className="truncate">{t.title}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="px-4 py-2.5">
          <Link to="/app/approvals" className="text-xs font-medium text-brand-700 hover:underline">
            Open approvals & tasks →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  )
}
